/**
 * (owner, Sep 17: "only players near you can hear what you are saying; further you are, the quieter it gets; building walls
 * and floors should make the voice quieter, so people inside the same building and floor hear each other well, but a player
 * outside can only faintly hear the talk that goes on inside — and vice versa; the same for being inside a vehicle: inside
 * the same car → clearly") How loud one player's voice reaches another, from where the two stand: a pure rule, the same on
 * every client, so what you hear of someone is what they see you hearing (the rings). docs/DESIGN_VOICE.md.
 */
export interface Ear { x: number; y: number; /** inside a building: its plan key */ inside?: string; floor?: number; /** in a vehicle: its id, and whether the windows are down */ vehicle?: number; windows?: boolean; dead?: boolean }
/** as far as a voice carries in the open, metres — beyond it nothing */
export const VOICE_RANGE = 40;
/** up to this close a voice is at full volume */
export const VOICE_NEAR = 3;
/** indoors the room is the range: the same building and floor, a voice carries this far */
export const VOICE_RANGE_INDOORS = 24;
/** a wall between the two: this much of the voice gets through, muffled */
export const WALL = 0.25;
/** a floor between the two, on top of the walls' share: each floor apart halves it */
export const FLOOR = 0.5;
/** a car's body between the two: with the windows up; with them down */
export const CAR_CLOSED = 0.3, CAR_OPEN = 0.7;

/** what `b` hears of `a`: `gain` 0..1, and `muffle` 0..1 — how much of the treble a wall or a car body takes (1 = a closed wall) */
export function hearing(a: Ear, b: Ear): { gain: number; muffle: number } {
  if (a.dead || b.dead) return { gain: 0, muffle: 0 };
  if (a.vehicle !== undefined && a.vehicle === b.vehicle) return { gain: 1, muffle: 0 }; // the same car: clearly, whatever the distance (a long bus)
  const d = Math.hypot(a.x - b.x, a.y - b.y);
  let through = 1, muffle = 0, range = VOICE_RANGE;
  if (a.inside && b.inside && a.inside === b.inside) {
    range = VOICE_RANGE_INDOORS; // the same building: the rooms
    const floors = Math.abs((a.floor ?? 0) - (b.floor ?? 0));
    if (floors) { through *= WALL * FLOOR ** (floors - 1); muffle = 1; } // a floor between: like a wall, and each further floor halves it
  } else if (a.inside || b.inside) {
    through *= a.inside && b.inside ? WALL * WALL : WALL; muffle = 1; // one indoors, or two different buildings (two walls)
  }
  for (const e of [a, b]) if (e.vehicle !== undefined) { through *= e.windows ? CAR_OPEN : CAR_CLOSED; muffle = Math.max(muffle, e.windows ? 0.3 : 0.8); }
  if (d >= range) return { gain: 0, muffle };
  const falloff = d <= VOICE_NEAR ? 1 : 1 - (d - VOICE_NEAR) / (range - VOICE_NEAR);
  return { gain: Math.max(0, Math.min(1, falloff * through)), muffle };
}
/** the low-pass a muffled voice goes through, Hz: a closed wall or a shut car leaves the mumble, an open window most of the voice */
export function muffleHz(muffle: number): number { return muffle <= 0 ? 20000 : 4000 - 3200 * Math.min(1, muffle); }
